/**
 * Réseaux sociaux du tenant (étape 2 — parité legacy, apprentissage M12).
 *
 * Règle d'or : un réseau vide est MASQUÉ (jamais d'icône morte). WhatsApp et
 * Google sont optionnels comme les autres. La note Google/Uber est gérée à part
 * (badge conditionnel dans MenuView), ici on ne traite que les liens.
 *
 * Fonctions pures et testables.
 */

import type { TenantSocials } from './database.types';

export type SocialKey =
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'google'
  | 'whatsapp'
  | 'youtube'
  | 'snapchat';

export interface SocialNetwork {
  key: SocialKey;
  label: string;
  /** Couleur de marque (fallback si pas de gradient). */
  color: string;
  /** Dégradé de marque (priorité sur color). */
  gradient: string;
}

/** Liste ordonnée des réseaux pour l'affichage (ordre + label + couleurs). */
export const SOCIAL_NETWORKS: SocialNetwork[] = [
  { key: 'facebook', label: 'Facebook', color: '#1877F2', gradient: 'linear-gradient(135deg, #1877F2, #0D5DBC)' },
  { key: 'instagram', label: 'Instagram', color: '#E1306C', gradient: 'linear-gradient(135deg, #F58529, #DD2A7B, #8134AF, #515BD4)' },
  { key: 'tiktok', label: 'TikTok', color: '#000000', gradient: 'linear-gradient(135deg, #25F4EE, #000000, #FE2C55)' },
  { key: 'google', label: 'Google Maps', color: '#4285F4', gradient: 'linear-gradient(135deg, #4285F4, #34A853, #FBBC05, #EA4335)' },
  { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', gradient: 'linear-gradient(135deg, #25D366, #128C7E)' },
  { key: 'youtube', label: 'YouTube', color: '#FF0000', gradient: 'linear-gradient(135deg, #FF0000, #C20000)' },
  { key: 'snapchat', label: 'Snapchat', color: '#FFFC00', gradient: 'linear-gradient(135deg, #FFFC00, #FFD600)' },
];

export interface ActiveSocial extends SocialNetwork {
  url: string;
}

/**
 * Renvoie les réseaux renseignés (URL non vide), dans l'ordre de SOCIAL_NETWORKS.
 * Les réseaux absents ou vides sont masqués (M12).
 */
export function getActiveSocials(socials: TenantSocials | null | undefined): ActiveSocial[] {
  if (!socials) return [];
  return SOCIAL_NETWORKS.filter((n) => {
    const url = socials[n.key];
    return typeof url === 'string' && url.trim().length > 0;
  }).map((n) => ({ ...n, url: (socials[n.key] as string).trim() }));
}
